import { coolerConfigFor } from '@1delta/data-sdk'
import { CoolerMonoAbi, CoolerLtvOracleAbi } from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import type { CoolerDripRaw, CoolerMarketsRaw } from './types'

/**
 * Public-data fetch for Olympus Cooler V2 (MonoCooler) — ONE flat on-chain
 * multicall, no API branch (nothing off-chain exists for this protocol).
 * There is exactly one market ever — gOHM collateral against the live
 * `debtToken()` on the monolithic MonoCooler — so there is no roster and no
 * fan-out: `COOLER` is both the dispatch key and the market key.
 *
 * Two reads that a naive port would get wrong:
 *  - `debtToken()` is read LIVE and preferred over the config seed's `usds`:
 *    a new CoolerTreasuryBorrower can swap the debt token (docs anticipate
 *    USDC), and every MonoCooler amount stays WAD regardless.
 *  - The instant borrow headroom is `sUSDS.maxWithdraw(treasury)` — the
 *    treasury's sUSDS buffer, ~5.75M USDS today against a $126M book. It is
 *    the only cap; there is no debt ceiling to read.
 */

/** Minimal ERC-4626 fragment — the treasury's instant borrow headroom. */
const SUSDS_MAX_WITHDRAW_ABI = [
  {
    name: 'maxWithdraw',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

const COOLER_PUBLIC_READ_ABI = [
  ...CoolerMonoAbi,
  ...CoolerLtvOracleAbi,
  ...SUSDS_MAX_WITHDRAW_ABI,
] as any

const big = (v: any): bigint | null => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number') return BigInt(v)
    if (typeof v === 'string' && /^(0x[0-9a-fA-F]+|\d+)$/.test(v))
      return BigInt(v)
  } catch {}
  return null
}

export async function fetchCoolerMarkets(
  lender: string,
  chainId: string,
): Promise<CoolerMarketsRaw> {
  const config = coolerConfigFor(lender, chainId)
  const empty: CoolerMarketsRaw = {
    lender,
    config,
    totalCollateral: null,
    totalDebt: null,
    interestRateWad: null,
    oltvPrice: null,
    lltvPrice: null,
    minDebtRequired: null,
    borrowsPaused: null,
    liquidationsPaused: null,
    debtToken: null,
    collateralToken: null,
    drip: null,
    liquidationLtvPremiumBps: null,
    susdsHeadroom: null,
    source: 'none',
  }
  if (!config?.monoCooler) return empty

  const mc = config.monoCooler
  const calls = [
    { address: mc, name: 'totalCollateral', params: [] },
    { address: mc, name: 'totalDebt', params: [] },
    { address: mc, name: 'interestRateWad', params: [] },
    { address: mc, name: 'loanToValues', params: [] },
    { address: mc, name: 'minDebtRequired', params: [] },
    { address: mc, name: 'borrowsPaused', params: [] },
    { address: mc, name: 'liquidationsPaused', params: [] },
    { address: mc, name: 'debtToken', params: [] },
    { address: mc, name: 'collateralToken', params: [] },
    { address: config.ltvOracle, name: 'originationLtvData', params: [] },
    { address: config.ltvOracle, name: 'liquidationLtvPremiumBps', params: [] },
    { address: config.susds, name: 'maxWithdraw', params: [config.treasury] },
  ]

  try {
    const results = (await multicallRetryUniversal({
      chain: chainId,
      calls,
      abi: COOLER_PUBLIC_READ_ABI,
      allowFailure: true,
    })) as any[]

    // Multi-output reads decode as arrays: [maxOriginationLtv, liquidationLtv]
    // and [startingValue, startTime, targetValue, targetTime, slope].
    const ltvs = results[3]
    const oltvPrice = Array.isArray(ltvs) ? big(ltvs[0]) : null
    const lltvPrice = Array.isArray(ltvs) ? big(ltvs[1]) : null

    const dripRaw = results[9]
    let drip: CoolerDripRaw | null = null
    if (Array.isArray(dripRaw) && dripRaw.length >= 5) {
      const [startingValue, startTime, targetValue, targetTime, slope] =
        dripRaw.map(big)
      if (
        startingValue !== null &&
        startTime !== null &&
        targetValue !== null &&
        targetTime !== null &&
        slope !== null
      ) {
        drip = { startingValue, startTime, targetValue, targetTime, slope }
      }
    }

    const premium = big(results[10])

    return {
      lender,
      config,
      totalCollateral: big(results[0]),
      totalDebt: big(results[1]),
      interestRateWad: big(results[2]),
      oltvPrice,
      lltvPrice,
      minDebtRequired: big(results[4]),
      borrowsPaused: typeof results[5] === 'boolean' ? results[5] : null,
      liquidationsPaused: typeof results[6] === 'boolean' ? results[6] : null,
      debtToken:
        typeof results[7] === 'string' && results[7].startsWith('0x')
          ? results[7]
          : null,
      collateralToken:
        typeof results[8] === 'string' && results[8].startsWith('0x')
          ? results[8]
          : null,
      drip,
      liquidationLtvPremiumBps: premium !== null ? Number(premium) : null,
      susdsHeadroom: big(results[11]),
      source: 'chain',
    }
  } catch {
    return empty
  }
}
